import { parseCourseModuleAppearance, type CourseModuleAppearance, type CourseModuleAppearanceUpdate, type CardAppearancePatch, type LockAction } from '@/shared';
import { api } from './api';
import type { PageSection, CourseMenuItem } from './course-appearance.service';
import type { LessonProgress } from './progress.service';

export interface Product {
  access?: ContentAccess;
  cardAppearance?: CardAppearancePatch;
  id: string;
  /** Produto (área de membros) a que a formação pertence. */
  memberAreaId?: string | null;
  title: string;
  slug: string;
  description: string;
  shortDescription?: string;
  thumbnail?: string;
  thumbnailHorizontal?: string;
  thumbnailVertical?: string;
  bannerImage?: string;
  previewVideo?: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  navigationMode?: 'MODULES' | 'LESSON';
  isFeatured?: boolean;
  instructorId?: string;
  instructor?: {
    id: string;
    name: string;
    avatar?: string;
  };
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  modules?: ProductModule[];
  _count?: { modules?: number; enrollments?: number; lessons?: number };
  /** Stored page-builder layout (from findBySlug). Empty/undefined = use default. */
  pageSections?: PageSection[];
  /** Per-course sidebar items (from findBySlug). Empty = fall back to global menu. */
  menuItems?: CourseMenuItem[];
  tags?: string[];
  studentsCount?: number;
  rating?: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ProductModule {
  access?: ContentAccess;
  cardAppearance?: CardAppearancePatch;
  id: string;
  title: string;
  description?: string;
  order: number;
  thumbnail?: string;
  isPublished?: boolean;
  parentModuleId?: string;
  parentModule?: { id: string; title: string };
  lessons?: Lesson[];
  children?: ProductModule[];
  lessonsCount?: number;
  products?: { id: string; title: string }[];
  metadata?: Record<string, unknown>;
  _count?: {
    lessons: number;
    products?: number;
    children?: number;
  };
}

export function courseModuleAppearance(course: Product): CourseModuleAppearance | null {
  return course.metadata?.moduleAppearance === undefined
    ? null
    : parseCourseModuleAppearance(course.metadata.moduleAppearance);
}

export interface PaginatedModules {
  data: ProductModule[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface Lesson {
  access?: ContentAccess;
  id: string;
  title: string;
  description?: string;
  type: 'VIDEO' | 'TEXT' | 'QUIZ' | 'ASSIGNMENT' | 'LIVE';
  order: number;
  duration?: number;
  videoUrl?: string;
  videoId?: string;
  videoProvider?: string;
  estimatedReadTime?: number;
  pdfUrl?: string;
  liveDate?: string;
  liveUrl?: string;
  module?: {
    courseId?: string;
    productId?: string;
    course?: { id?: string; slug?: string };
    product?: { id?: string; slug?: string };
  };
  materials?: Array<{ id: string; name: string; url: string; size: number; type?: string; description?: string }>;
  downloadFiles?: Lesson['materials'];
  content?: string;
  isFree?: boolean;
  completed?: boolean;
  _count?: {
    modules?: number;
  };
}

export interface ContentAccess {
  allowed: boolean;
  reason: string;
  visibility?: 'locked' | 'hidden';
  action?: LockAction;
  /** Só quando reason = 'expired': quando a última concessão acabou. */
  expiredAt?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ProductFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  categoryId?: string;
}

class ProductsService {
  async getAllForAdmin(): Promise<Product[]> {
    const first = await this.findAll({ page: 1, limit: 100 });
    const products = [...first.data];
    for (let page = 2; page <= (first.meta?.totalPages ?? 1); page++) {
      products.push(...(await this.findAll({ page, limit: 100 })).data);
    }
    return products;
  }

  async findAll(
    filters: ProductFilters = {},
  ): Promise<PaginatedResponse<Product>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        const paramKey = key === 'limit' ? 'perPage' : key;
        params.append(paramKey, String(value));
      }
    });

    const { data } = await api.get(`/products?${params.toString()}`);
    return { data: data.data || [], meta: data.meta };
  }

  async findBySlug(slug: string): Promise<Product> {
    const { data } = await api.get(`/products/slug/${slug}`);
    return data.data || data;
  }

  async findById(id: string): Promise<Product> {
    const { data } = await api.get(`/products/${id}`);
    return data.data || data;
  }

  async getModules(productId: string): Promise<ProductModule[]> {
    const { data } = await api.get(`/products/${productId}/modules`);
    return data.data || data || [];
  }

  async getModuleAppearance(productId: string): Promise<CourseModuleAppearance> {
    const { data } = await api.get(`/products/${productId}/module-appearance`);
    return parseCourseModuleAppearance(data.data || data);
  }

  async updateModuleAppearance(productId: string, patch: CourseModuleAppearanceUpdate): Promise<CourseModuleAppearance> {
    const { data } = await api.put(`/products/${productId}/module-appearance`, patch);
    return parseCourseModuleAppearance(data.data || data);
  }

  /** Single module (with its lessons), from any product. No per-user progress. */
  async getModuleById(id: string): Promise<ProductModule> {
    const { data } = await api.get(`/modules/${id}`);
    return data.data || data;
  }

  async getLesson(lessonId: string): Promise<Lesson> {
    const { data } = await api.get(`/lessons/${lessonId}`);
    return data.data || data;
  }

  async getLessonWithProgress(
    lessonId: string,
  ): Promise<Lesson & { progress: LessonProgress | null; completed: boolean }> {
    // Fetch lesson and progress separately since backend doesn't have combined endpoint
    const [lessonResponse, progressResponse] = await Promise.all([
      api.get(`/lessons/${lessonId}`),
      api.get(`/progress/lessons/${lessonId}`),
    ]);

    const lesson = lessonResponse.data.data || lessonResponse.data;
    const payload = progressResponse.data;
    const progress: LessonProgress | null =
      payload && typeof payload === 'object' && 'data' in payload
        ? payload.data
        : payload || null;

    return {
      ...lesson,
      completed: progress?.completed || false,
      progress,
    };
  }

  // Admin methods
  async create(product: Partial<Product>): Promise<Product> {
    const { data } = await api.post('/products', product);
    return data;
  }

  async update(id: string, product: Partial<Product>): Promise<Product> {
    const { data } = await api.put(`/products/${id}`, product);
    return data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  }

  async publish(id: string): Promise<Product> {
    const { data } = await api.post(`/products/${id}/publish`);
    return data;
  }

  async archive(id: string): Promise<Product> {
    const { data } = await api.post(`/products/${id}/archive`);
    return data;
  }

  async unarchive(id: string): Promise<Product> {
    const { data } = await api.post(`/products/${id}/unarchive`);
    return data;
  }

  async publishBulk(ids?: string[]): Promise<{ published: number }> {
    const { data } = await api.post('/products/publish-bulk', { ids });
    return data?.data || data;
  }

  async mergeToCourse(
    sourceIds: string[],
    targetId: string,
  ): Promise<{ converted: number; message: string }> {
    const { data } = await api.post('/products/merge-to-course', {
      sourceIds,
      targetId,
    });
    return data?.data || data;
  }

  // Module methods
  async createModule(
    productId: string,
    module: Partial<ProductModule>,
  ): Promise<ProductModule> {
    const { data } = await api.post(`/products/${productId}/modules`, module);
    return data;
  }

  async updateModule(
    moduleId: string,
    module: Partial<ProductModule>,
  ): Promise<ProductModule> {
    const { data } = await api.put(`/modules/${moduleId}`, module);
    return data;
  }

  async deleteModule(moduleId: string): Promise<void> {
    await api.delete(`/modules/${moduleId}`);
  }

  async reorderModules(productId: string, moduleIds: string[]): Promise<void> {
    await api.post(`/products/${productId}/modules/reorder`, { moduleIds });
  }

  async reorderChildModules(
    parentModuleId: string,
    moduleIds: string[],
  ): Promise<void> {
    const modules = moduleIds.map((id, index) => ({ id, order: index }));
    await api.post(`/modules/${parentModuleId}/children/reorder`, { modules });
  }

  // Lesson methods
  async createLesson(
    moduleId: string,
    lesson: Partial<Lesson>,
  ): Promise<Lesson> {
    const { data } = await api.post(`/modules/${moduleId}/lessons`, lesson);
    return data;
  }

  async updateLesson(
    lessonId: string,
    lesson: Partial<Lesson>,
  ): Promise<Lesson> {
    const { data } = await api.put(`/lessons/${lessonId}`, lesson);
    return data;
  }

  async deleteLesson(lessonId: string): Promise<void> {
    await api.delete(`/lessons/${lessonId}`);
  }

  async reorderLessons(moduleId: string, lessonIds: string[]): Promise<void> {
    await api.post(`/modules/${moduleId}/lessons/reorder`, { lessonIds });
  }

  // ===== Many-to-Many operations =====

  async getAllModules(search?: string): Promise<ProductModule[]> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const { data } = await api.get(`/modules${params}`);
    return data?.data || data || [];
  }

  async getModulesPaginated(params: {
    page?: number;
    perPage?: number;
    search?: string;
  }): Promise<PaginatedModules> {
    const { data } = await api.get('/modules/paginated', { params });
    return data as PaginatedModules;
  }

  async linkModuleToProduct(
    moduleId: string,
    productId: string,
    order?: number,
  ): Promise<void> {
    await api.post(`/modules/${moduleId}/link-product`, { productId, order });
  }

  async unlinkModuleFromProduct(
    moduleId: string,
    productId: string,
  ): Promise<void> {
    await api.delete(`/modules/${moduleId}/unlink-product/${productId}`);
  }

  async linkLessonToModule(
    lessonId: string,
    moduleId: string,
    order?: number,
  ): Promise<void> {
    await api.post(`/modules/${moduleId}/link-lesson`, { lessonId, order });
  }

  async unlinkLessonFromModule(
    lessonId: string,
    moduleId: string,
  ): Promise<void> {
    await api.delete(`/modules/${moduleId}/unlink-lesson/${lessonId}`);
  }

  async getAllLessons(search?: string): Promise<Lesson[]> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const { data } = await api.get(`/lessons${params}`);
    return data?.data || data || [];
  }

  async convertToModule(
    productId: string,
    targetProductId?: string,
  ): Promise<{ module: ProductModule; lessonsCount: number; message: string }> {
    const { data } = await api.post(
      `/products/${productId}/convert-to-module`,
      {
        targetProductId,
      },
    );
    return data?.data || data;
  }
}

export const productsService = new ProductsService();

// Backwards compatibility - export as coursesService too
export const coursesService = productsService;
